import React, { useContext, useEffect } from 'react'
import { t } from '../../i18n/i18n'
import { api } from '../../helpers/apiHelper'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { useForm } from 'react-hook-form'
import { ControlledTextInput } from '../common/inputs/TextInput'
import { ModalActions } from '../common/modal/Modal'
import { Button } from '@fluentui/react-components'
import { addWorkplace, deleteWorkplace, getWorkplaceOrgSizes, getWorkplaceSNIs, isSystemAdminOrCreator, updateWorkplace } from '../../helpers/settingsHelper'
import { OrganisationSize, Workplace } from '../../api/schemas/schema'
import { ControlledOrgSizeDropdown } from '../common/inputs/ControlledOrgSizeDropdown'
import { ControlledSniDropdown } from '../common/inputs/ControlledSniDropdown'
import { ControlledCfarInput } from '../common/inputs/ControlledCfarInput'
import { ControlledZipInput } from '../common/inputs/ControlledZipInput'
import { DataContext } from '../../context/DataContext'
import { DraggableModal } from '../common/modal/DraggableModal'
import { MoreOptionsMenuButton } from '../common/other/MoreOptionsMenuButton'
import { isSystemAdmin } from '../../helpers/permissionHelper'
import { Form } from '../common/form/Form'

type ModalUpsertWorkplaceProps = {
   setAllWorkplaces: React.Dispatch<React.SetStateAction<Workplace[]>>
   workplace: Workplace | null
   open: boolean
   setOpen: React.Dispatch<React.SetStateAction<boolean>>
   workplaceInUseMap?: Record<string, boolean>
}

export const ModalUpsertWorkplace = ({ setAllWorkplaces, workplace, open, setOpen, workplaceInUseMap }: ModalUpsertWorkplaceProps) => {
   const dataContext = useContext(DataContext)
   const { organisation, userAccount, hasMsalToken } = dataContext.state
   const {
      control,
      reset,
      handleSubmit,
      getValues,
      formState: { disabled, isDirty, isSubmitting },
   } = useForm<Workplace>({ disabled: workplace ? workplace.id && !isSystemAdminOrCreator(workplace, userAccount) : false })
   const [allOrgSizes, setAllOrgSizes] = React.useState<OrganisationSize[]>([])

   const close = () => {
      reset()
      setOpen(false)
   }
   useEffect(() => {
      if (workplace) {
         reset(workplace)
      } else {
         reset({
            name: null,
            cfar: null,
            organisationSize: null,
            snIs: [],
            description: null,
            address: null,
            postalCode: null,
            county: null,
         })
      }
   }, [reset, workplace])

   const onSubmit = async (data: Workplace) => {
      data.organisationSizeId = allOrgSizes.find((o) => o.text === data.organisationSize.text)?.id

      if (workplace?.id) {
         const updatedWorkplace = await updateWorkplace(close, setAllWorkplaces, dataContext, data)
         reset(updatedWorkplace)
      } else {
         await addWorkplace(close, setAllWorkplaces, dataContext, data)
      }
   }

   useEffect(() => {
      if (!open) return
      ;(async () => {
         const allOrgSizes = await api().getAllOrganisationSizes()
         setAllOrgSizes(allOrgSizes.data)
      })()
   }, [open])

   const deleteOptions = () => {
      const workplaceInUse = workplaceInUseMap[workplace.id] ?? false
      const userHasPermission = isSystemAdmin(userAccount)

      const deleteDisabled = !userHasPermission || workplaceInUse
      const deleteTooltipMessage = deleteDisabled
         ? !userHasPermission
            ? t('OnlySystemAdministratorCanDeleteWorkplaces')
            : t('TheWorkplaceIsInUseAndCannotBeDeleted')
         : ''

      return (
         <MoreOptionsMenuButton
            item={workplace}
            deleteFunction={deleteWorkplace}
            setState={setAllWorkplaces}
            disabled={deleteDisabled}
            disabledToolTipMessage={deleteTooltipMessage}
            deleteMessage={t('DeleteItem', { Item: t('Workplace').toLowerCase() })}
            deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('TheWorkplace').toLocaleLowerCase() })}
            closeOnDelete={close}
         />
      )
   }

   return (
      <DraggableModal
         title={workplace ? t('EditItem', { Item: t('Workplace') }) : t('NewWorkplace')}
         open={open}
         onClose={close}
         secondaryButton={workplace ? deleteOptions : null}
      >
         <Form onSubmit={handleSubmit(onSubmit)}>
            <Grid>
               <GridItem size="12/12">
                  <ControlledTextInput label={t('Name')} control={control} name="name" required />
               </GridItem>
               <GridItem size="6/12">
                  <ControlledCfarInput label={t('CFAR')} control={control} name="cfar" defaultValue={getValues('cfar')} />
               </GridItem>
               <GridItem size="6/12">
                  <ControlledOrgSizeDropdown
                     name="organisationSize"
                     options={getWorkplaceOrgSizes(allOrgSizes, organisation)}
                     label={t('NumberOfEmployees')}
                     control={control}
                     required
                  />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledSniDropdown
                     label={t('SNICodes')}
                     control={control}
                     name="snIs"
                     options={organisation.snIs && getWorkplaceSNIs(organisation)}
                     required
                  />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledTextInput label={t('WorkplaceDescription')} control={control} name="description" multiline />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledTextInput label={t('Address')} control={control} name="address" />
               </GridItem>
               <GridItem size="6/12">
                  <ControlledZipInput label={t('PostalCode')} control={control} name="postalCode" />
               </GridItem>
               <GridItem size="6/12">
                  <ControlledTextInput label={t('County')} control={control} name="county" />
               </GridItem>

               <GridItem size="12/12">
                  <ModalActions>
                     <Button onClick={close} disabled={isSubmitting}>
                        {disabled ? t('Close') : t('Cancel')}
                     </Button>

                     {!disabled && (
                        <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                           {t(workplace ? 'Update' : 'Save')}
                        </Button>
                     )}
                  </ModalActions>
               </GridItem>
            </Grid>
         </Form>
      </DraggableModal>
   )
}
